/* ══════════════════════════════════════════════
   CLIENT STORES — COLLECTION PAGE JS
   ══════════════════════════════════════════════
   - Category / sub-category / price / color / search / sort filters
   - "Show More Products" client-side pagination
   - Quick-shop modal (fetches /products/{handle}.js)
   - Personalization UI (drinkware) with fee add-on
   - Cart drawer (slide-in) driven by /cart.js
   ══════════════════════════════════════════════ */

(function () {
  'use strict';

  var root = document.querySelector('[data-cs-coll]');
  if (!root) return;

  var grid = document.getElementById('cs-product-grid');
  if (!grid) return;

  // ─────────────── Shared helpers ───────────────
  function slice(nodeList) { return Array.prototype.slice.call(nodeList); }
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $$(sel, ctx) { return slice((ctx || root).querySelectorAll(sel)); }

  var feeVariantId = root.getAttribute('data-fee-variant-id') || '';
  var feePrice = parseInt(root.getAttribute('data-fee-price'), 10) || 0;
  var personalizableTag = root.getAttribute('data-personalizable-tag') || 'personalizable';
  var pageSize = parseInt(root.getAttribute('data-page-size'), 10) || 24;

  function formatMoney(cents) {
    var n = (cents || 0) / 100;
    return '$' + n.toFixed(2);
  }

  // Canonical color map → hex. Keys are lowercase with spaces stripped.
  // If a product uses a color name that's not in the map we fall back to
  // using the raw color value as a CSS color (handles "red", "white",
  // "skyblue", "#rrggbb", etc.) and finally to a neutral grey.
  var COLOR_MAP = {
    'black': '#000000', 'jetblack': '#000000',
    'white': '#ffffff', 'offwhite': '#f5f5f0', 'cream': '#f5f5dc', 'ivory': '#fffff0', 'natural': '#ece8d8',
    'red': '#dc2626', 'truered': '#dc2626', 'cardinal': '#b91c1c', 'cardinalred': '#b91c1c', 'cherry': '#c41e3a',
    'darkred': '#991b1b', 'crimson': '#b91c1c', 'brickred': '#9b2e2a',
    'maroon': '#7f1d1d', 'burgundy': '#7c1d1d', 'wine': '#722f37',
    'pink': '#ec4899', 'hotpink': '#ec4899', 'lightpink': '#f9a8d4', 'fuchsia': '#e11d74', 'magenta': '#d946ef',
    'rose': '#fb7185', 'coral': '#fb7185', 'salmon': '#fa8072',
    'orange': '#f97316', 'burntorange': '#cc5500', 'darkorange': '#ea580c',
    'yellow': '#facc15', 'gold': '#eab308', 'metallicgold': '#b8860b', 'mustard': '#d4a017',
    'lime': '#84cc16', 'limegreen': '#84cc16',
    'green': '#16a34a', 'kellygreen': '#16a34a', 'irishgreen': '#15803d',
    'darkgreen': '#14532d', 'forestgreen': '#166534', 'forest': '#166534', 'huntergreen': '#355e3b',
    'olive': '#6b7a27', 'army': '#4b5320', 'military': '#5a6e3a', 'militarygreen': '#5a6e3a',
    'mint': '#98d8b1', 'seafoam': '#9fe2bf', 'teal': '#14b8a6', 'turquoise': '#30d5c8',
    'aqua': '#00ffff', 'cyan': '#06b6d4',
    'blue': '#3b82f6', 'royal': '#2563eb', 'royalblue': '#2563eb', 'trueroyal': '#2563eb',
    'navy': '#1e3a8a', 'navyblue': '#1e3a8a', 'midnight': '#0f1c3d',
    'carolina': '#7bafd4', 'carolinablue': '#7bafd4', 'lightblue': '#7bafd4', 'skyblue': '#87ceeb',
    'columbia': '#9bcbeb', 'columbiablue': '#9bcbeb', 'powder': '#b0e0e6', 'powderblue': '#b0e0e6',
    'purple': '#9333ea', 'violet': '#8b5cf6', 'lavender': '#c4b5fd', 'plum': '#6b2e8f',
    'grey': '#9ca3af', 'gray': '#9ca3af', 'heathergrey': '#b0b7bf', 'heathergray': '#b0b7bf',
    'sportgrey': '#b0b7bf', 'sportgray': '#b0b7bf', 'athleticheather': '#c8ccd4',
    'charcoal': '#374151', 'graphite': '#41454c', 'slate': '#4b5563', 'gunmetal': '#2c3539',
    'silver': '#c0c7cf', 'platinum': '#d6d6d6',
    'brown': '#7b3f00', 'chocolate': '#3e2723', 'tan': '#d2b48c', 'khaki': '#c3b091', 'sand': '#d7c9a7',
    'beige': '#e7dcc5',
    // Leather patch tones
    'rawhide': '#c8833a', 'caramel': '#c8833a',
    'lightbrown': '#d4962a',
    'darkbrown': '#7a5230'
  };

  function swatchHex(rawValue) {
    if (!rawValue) return null;
    var raw = String(rawValue).trim();
    var key = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    // Try a few progressive strips: "athleticheatherred" → "red"
    var suffixes = ['red', 'blue', 'green', 'black', 'white', 'grey', 'gray', 'pink', 'gold', 'yellow', 'orange', 'purple'];
    for (var i = 0; i < suffixes.length; i++) {
      if (key.indexOf(suffixes[i]) !== -1 && COLOR_MAP[suffixes[i]]) return COLOR_MAP[suffixes[i]];
    }
    // If the raw value is already a valid CSS color (red, #fff, rgb(...)), let the browser handle it.
    var probe = document.createElement('div');
    probe.style.color = '';
    probe.style.color = raw;
    if (probe.style.color !== '') return raw;
    return null;
  }

  // ─────────────────────────────────────────────
  //  FILTERS + SORT + SHOW MORE PAGINATION
  // ─────────────────────────────────────────────
  var resultsEl = document.getElementById('cs-results-text');
  var noResults = document.getElementById('cs-no-results');
  var sortSelect = document.getElementById('cs-sort-select');
  var searchInput = $('[data-cs-search]');
  var catBtns = $$('.cs-cat-btn');
  var sideCatBtns = $$('.cs-side-cat');
  var countEls = $$('.cs-cat-count');
  var catWraps = $$('.cs-cat-wrap');
  var subItems = $$('.cs-cat-dropdown__item');
  var colorSwatches = $$('.cs-color-swatch');
  var priceMinInput = $('[data-cs-price-min]');
  var priceMaxInput = $('[data-cs-price-max]');
  var priceApplyBtn = $('[data-cs-price-apply]');
  var pricePresets = $$('.cs-price-range__preset');
  var accordionBtns = $$('[data-cs-accordion]');
  var resetBtn = $('[data-cs-reset]');
  var filtersToggle = $('[data-cs-filters-toggle]');
  var searchToggle = $('[data-cs-search-toggle]');
  var topnavInner = $('.cs-topnav__inner');
  var sidebar = $('[data-cs-sidebar]');
  var sidebarOverlay = $('[data-cs-sidebar-overlay]');
  var sidebarClose = $('[data-cs-sidebar-close]');
  var loadMoreBtn = $('[data-cs-load-more]');
  var loadMoreRemaining = $('[data-cs-remaining]');
  var filterChipsEl = $('[data-cs-filter-chips]');

  var products = $$('.cs-product', grid);

  var state = {
    cat: 'all',
    sub: null,
    priceMin: null,
    priceMax: null,
    colors: [],
    search: '',
    sort: 'featured',
    shownCount: pageSize
  };

  function updateCounts() {
    var counts = { all: products.length };
    products.forEach(function (p) {
      (p.getAttribute('data-cat') || '').split(/\s+/).forEach(function (c) {
        if (!c) return;
        counts[c] = (counts[c] || 0) + 1;
      });
    });
    countEls.forEach(function (el) {
      var key = el.getAttribute('data-count');
      el.textContent = counts[key] != null ? counts[key] : 0;
    });
  }

  function matches(p) {
    if (state.cat !== 'all') {
      var pCats = (p.getAttribute('data-cat') || '').split(/\s+/);
      if (pCats.indexOf(state.cat) === -1) return false;
    }
    if (state.sub) {
      if ((p.getAttribute('data-sub') || '') !== state.sub) return false;
    }
    var price = parseFloat(p.getAttribute('data-price')) || 0;
    if (state.priceMin != null && price < state.priceMin) return false;
    if (state.priceMax != null && price > state.priceMax) return false;
    if (state.colors.length > 0) {
      var pColors = (p.getAttribute('data-colors') || '').split(/\s+/);
      var hit = false;
      for (var i = 0; i < state.colors.length; i++) {
        if (pColors.indexOf(state.colors[i]) !== -1) { hit = true; break; }
      }
      if (!hit) return false;
    }
    if (state.search) {
      var title = p.getAttribute('data-title') || '';
      if (title.indexOf(state.search) === -1) return false;
    }
    return true;
  }

  function sortList(list) {
    var mode = state.sort;
    var copy = list.slice();
    if (mode === 'featured') {
      copy.sort(function (a, b) {
        return (parseInt(a.getAttribute('data-index'), 10) || 0)
             - (parseInt(b.getAttribute('data-index'), 10) || 0);
      });
    } else if (mode === 'low' || mode === 'high') {
      copy.sort(function (a, b) {
        var pa = parseFloat(a.getAttribute('data-price')) || 0;
        var pb = parseFloat(b.getAttribute('data-price')) || 0;
        return mode === 'low' ? pa - pb : pb - pa;
      });
    } else if (mode === 'az' || mode === 'za') {
      copy.sort(function (a, b) {
        var ta = a.getAttribute('data-title') || '';
        var tb = b.getAttribute('data-title') || '';
        if (ta < tb) return mode === 'az' ? -1 : 1;
        if (ta > tb) return mode === 'az' ? 1 : -1;
        return 0;
      });
    }
    return copy;
  }

  function apply(resetShown) {
    if (resetShown !== false) {
      state.shownCount = pageSize;
    }

    var filtered = products.filter(matches);
    var ordered = sortList(filtered);
    var total = ordered.length;
    var visible = ordered.slice(0, state.shownCount);

    products.forEach(function (p) { p.style.display = 'none'; });
    visible.forEach(function (p) {
      p.style.display = '';
      grid.appendChild(p);
    });

    if (resultsEl) {
      resultsEl.innerHTML = 'Showing <strong>' + visible.length + '</strong> of <strong>' + total + '</strong> products';
    }
    if (noResults) noResults.hidden = total > 0;

    if (loadMoreBtn) {
      var remaining = Math.max(0, total - visible.length);
      loadMoreBtn.hidden = remaining === 0;
      if (loadMoreRemaining) loadMoreRemaining.textContent = remaining;
    }

    // Sync active states
    catBtns.forEach(function (b) {
      var c = b.getAttribute('data-cat');
      b.classList.toggle('is-active', c === state.cat && !state.sub);
    });
    sideCatBtns.forEach(function (b) {
      var c = b.getAttribute('data-cat');
      b.classList.toggle('is-active', c === state.cat);
    });
    subItems.forEach(function (b) {
      var s = b.getAttribute('data-sub');
      b.classList.toggle('is-active', state.sub != null && s === state.sub);
    });
    colorSwatches.forEach(function (sw) {
      var c = sw.getAttribute('data-color');
      sw.classList.toggle('is-active', state.colors.indexOf(c) !== -1);
    });
    pricePresets.forEach(function (btn) {
      var min = parseFloat(btn.getAttribute('data-min'));
      var max = parseFloat(btn.getAttribute('data-max'));
      btn.classList.toggle('is-active', state.priceMin === min && state.priceMax === max);
    });

    renderFilterChips();
  }

  // Map category keys → user-facing label from the top-nav buttons
  function categoryLabel(key) {
    var btn = catBtns.filter(function (b) { return b.getAttribute('data-cat') === key; })[0];
    if (!btn) {
      btn = sideCatBtns.filter(function (b) { return b.getAttribute('data-cat') === key; })[0];
    }
    if (!btn) return key;
    // Clone, strip the count span, return the text
    var clone = btn.cloneNode(true);
    var count = clone.querySelector('.cs-cat-count');
    if (count) count.remove();
    var chev = clone.querySelector('.cs-cat-btn__chev');
    if (chev) chev.remove();
    return (clone.textContent || '').trim();
  }

  function renderFilterChips() {
    if (!filterChipsEl) return;
    var chips = [];
    if (state.cat && state.cat !== 'all') {
      chips.push({ label: categoryLabel(state.cat), clear: function () {
        state.cat = 'all';
        state.sub = null;
        apply();
      } });
    }
    if (state.sub) {
      chips.push({ label: state.sub.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }), clear: function () {
        state.sub = null;
        apply();
      } });
    }
    if (state.priceMin != null || state.priceMax != null) {
      var lo = state.priceMin != null ? '$' + state.priceMin : '$0';
      var hi = state.priceMax != null ? '$' + state.priceMax : 'Any';
      chips.push({ label: lo + '–' + hi, clear: function () {
        state.priceMin = null;
        state.priceMax = null;
        if (priceMinInput) priceMinInput.value = '';
        if (priceMaxInput) priceMaxInput.value = '';
        apply();
      } });
    }
    state.colors.forEach(function (color) {
      chips.push({
        label: color.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }),
        clear: function () {
          state.colors = state.colors.filter(function (c) { return c !== color; });
          apply();
        }
      });
    });
    if (state.search) {
      chips.push({ label: '"' + state.search + '"', clear: function () {
        state.search = '';
        if (searchInput) searchInput.value = '';
        apply();
      } });
    }

    filterChipsEl.innerHTML = '';
    if (chips.length === 0) {
      filterChipsEl.hidden = true;
      return;
    }
    filterChipsEl.hidden = false;
    chips.forEach(function (c) {
      var el = document.createElement('button');
      el.type = 'button';
      el.className = 'cs-filter-chip';
      el.innerHTML = '<span class="cs-filter-chip__label"></span><span class="cs-filter-chip__x" aria-hidden="true">×</span>';
      el.querySelector('.cs-filter-chip__label').textContent = c.label;
      el.setAttribute('aria-label', 'Remove filter: ' + c.label);
      el.addEventListener('click', c.clear);
      filterChipsEl.appendChild(el);
    });
    // "Clear all" link when multiple chips are showing
    if (chips.length > 1 && resetBtn) {
      var clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.className = 'cs-filter-chip cs-filter-chip--clear';
      clearAll.textContent = 'Clear all';
      clearAll.addEventListener('click', function () { resetBtn.click(); });
      filterChipsEl.appendChild(clearAll);
    }
  }

  // Dropdowns
  //
  // On mobile the pills sit inside an `overflow-x: auto` row, which CSS
  // forces to also clip the Y axis — so an absolute-positioned dropdown
  // below the button is invisible. To work around it we pin the dropdown
  // with `position: fixed` (via the `.cs-cat-dropdown--floating` class)
  // and compute its top from the button's bounding rect.
  function isMobileViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 1023px)').matches;
  }

  function positionDropdownFloating(btn, dd) {
    var rect = btn.getBoundingClientRect();
    dd.classList.add('cs-cat-dropdown--floating');
    dd.style.top = (rect.bottom + 6) + 'px';
  }

  function clearDropdownFloating(dd) {
    dd.classList.remove('cs-cat-dropdown--floating');
    dd.style.top = '';
  }

  function closeAllDropdowns() {
    catWraps.forEach(function (w) {
      var dd = w.querySelector('.cs-cat-dropdown');
      if (dd) {
        dd.hidden = true;
        clearDropdownFloating(dd);
      }
      w.classList.remove('is-open');
    });
  }

  catWraps.forEach(function (wrap) {
    var btn = wrap.querySelector('.cs-cat-btn');
    var dd = wrap.querySelector('.cs-cat-dropdown');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (btn.hasAttribute('data-has-dropdown') && dd) {
        var willOpen = dd.hidden;
        closeAllDropdowns();
        if (willOpen) {
          dd.hidden = false;
          wrap.classList.add('is-open');
          if (isMobileViewport()) positionDropdownFloating(btn, dd);
        }
      }
      state.cat = btn.getAttribute('data-cat') || 'all';
      state.sub = null;
      apply();
    });
  });

  // Re-anchor any open floating dropdown when the viewport or scroll changes
  window.addEventListener('resize', closeAllDropdowns);
  window.addEventListener('scroll', function () {
    catWraps.forEach(function (w) {
      if (!w.classList.contains('is-open')) return;
      var btn = w.querySelector('.cs-cat-btn');
      var dd = w.querySelector('.cs-cat-dropdown');
      if (btn && dd && dd.classList.contains('cs-cat-dropdown--floating')) {
        positionDropdownFloating(btn, dd);
      }
    });
  }, { passive: true });

  subItems.forEach(function (item) {
    item.addEventListener('click', function (e) {
      e.stopPropagation();
      state.cat = item.getAttribute('data-cat') || 'all';
      state.sub = item.getAttribute('data-sub') || null;
      closeAllDropdowns();
      apply();
    });
  });

  document.addEventListener('click', closeAllDropdowns);

  catBtns.forEach(function (btn) {
    if (btn.closest('.cs-cat-wrap')) return;
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      state.cat = btn.getAttribute('data-cat') || 'all';
      state.sub = null;
      closeAllDropdowns();
      apply();
    });
  });

  sideCatBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.cat = btn.getAttribute('data-cat') || 'all';
      state.sub = null;
      closeAllDropdowns();
      apply();
    });
  });

  // Price range
  function applyPriceFromInputs() {
    var min = parseFloat(priceMinInput && priceMinInput.value);
    var max = parseFloat(priceMaxInput && priceMaxInput.value);
    state.priceMin = isNaN(min) ? null : min;
    state.priceMax = isNaN(max) ? null : max;
    apply();
  }
  if (priceApplyBtn) priceApplyBtn.addEventListener('click', applyPriceFromInputs);
  [priceMinInput, priceMaxInput].forEach(function (el) {
    if (!el) return;
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); applyPriceFromInputs(); }
    });
  });
  pricePresets.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var min = parseFloat(btn.getAttribute('data-min'));
      var max = parseFloat(btn.getAttribute('data-max'));
      var same = state.priceMin === min && state.priceMax === max;
      if (same) {
        state.priceMin = null; state.priceMax = null;
        if (priceMinInput) priceMinInput.value = '';
        if (priceMaxInput) priceMaxInput.value = '';
      } else {
        state.priceMin = min; state.priceMax = max;
        if (priceMinInput) priceMinInput.value = min;
        if (priceMaxInput) priceMaxInput.value = max;
      }
      apply();
    });
  });

  // Colors
  colorSwatches.forEach(function (sw) {
    // Paint sidebar swatches from the shared color map
    var label = sw.getAttribute('title') || sw.getAttribute('data-color') || '';
    var hex = swatchHex(label);
    if (hex) {
      sw.style.background = hex;
      if (/^#(f|e)[0-9a-f]/i.test(hex) || hex === '#ffffff') {
        sw.style.borderColor = '#d1d5db';
      }
    } else {
      sw.style.background = 'repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 4px,#f3f4f6 4px,#f3f4f6 8px)';
    }
    sw.addEventListener('click', function () {
      var color = sw.getAttribute('data-color');
      var idx = state.colors.indexOf(color);
      if (idx === -1) state.colors.push(color);
      else state.colors.splice(idx, 1);
      apply();
    });
  });

  // Search (debounced)
  if (searchInput) {
    var searchTimer = null;
    searchInput.addEventListener('input', function () {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        state.search = (searchInput.value || '').trim().toLowerCase();
        apply();
      }, 120);
    });
  }

  // Sort
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      state.sort = sortSelect.value || 'featured';
      apply();
    });
  }

  // Accordion
  accordionBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var group = btn.closest('.cs-sidebar__group');
      if (group) group.classList.toggle('is-open');
    });
  });

  // Mobile filter drawer
  function openSidebar() {
    if (sidebar) sidebar.classList.add('is-open');
    if (sidebarOverlay) sidebarOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function closeSidebarFn() {
    if (sidebar) sidebar.classList.remove('is-open');
    if (sidebarOverlay) sidebarOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }
  if (filtersToggle) filtersToggle.addEventListener('click', openSidebar);
  if (sidebarClose) sidebarClose.addEventListener('click', closeSidebarFn);
  if (sidebarOverlay) sidebarOverlay.addEventListener('click', closeSidebarFn);

  // Mobile: toggle the search drawer
  if (searchToggle && topnavInner) {
    searchToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      var willOpen = !topnavInner.classList.contains('is-search-open');
      topnavInner.classList.toggle('is-search-open', willOpen);
      if (willOpen && searchInput) {
        setTimeout(function () { searchInput.focus(); }, 50);
      }
    });
  }

  // Show more
  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', function () {
      state.shownCount += pageSize;
      apply(false);
    });
  }

  // Reset
  if (resetBtn) {
    resetBtn.addEventListener('click', function () {
      state.cat = 'all';
      state.sub = null;
      state.priceMin = null;
      state.priceMax = null;
      state.colors = [];
      state.search = '';
      if (priceMinInput) priceMinInput.value = '';
      if (priceMaxInput) priceMaxInput.value = '';
      if (searchInput) searchInput.value = '';
      apply();
    });
  }

  // ─────────────────────────────────────────────
  //  QUICK SHOP MODAL
  // ─────────────────────────────────────────────
  var modal = $('[data-cs-modal]');
  var modalImage = $('[data-cs-modal-image]');
  var modalThumbs = $('[data-cs-modal-thumbs]');
  var modalTitle = $('[data-cs-modal-title]');
  var modalOptions = $('[data-cs-modal-options]');
  var modalPrice = $('[data-cs-modal-price]');
  var modalQtyInput = $('[data-cs-modal-qty]');
  var modalQtyMinus = $('[data-cs-modal-qty-minus]');
  var modalQtyPlus = $('[data-cs-modal-qty-plus]');
  var modalAdd = $('[data-cs-modal-add]');
  var modalDescription = $('[data-cs-modal-description]');
  var modalPersonalize = $('[data-cs-modal-personalize]');
  var modalPersonalizeEnabled = $('[data-cs-modal-personalize-enabled]');
  var modalPersonalizeBody = $('[data-cs-modal-personalize-body]');
  var modalPersonalizeFee = $('[data-cs-modal-personalize-fee]');
  var modalFont = $('[data-cs-modal-font]');
  var modalText = $('[data-cs-modal-text]');
  var modalCharCount = $('[data-cs-modal-char-count]');
  var modalPreview = $('[data-cs-modal-preview]');
  var modalPreviewText = $('[data-cs-modal-preview-text]');

  // Default the client-visible fee to $5 even if a fee product isn't wired
  // up — that way the UI and price total are consistent. The cart only
  // actually charges the fee when a fee product is configured.
  if (feePrice <= 0) feePrice = 500;

  if (modalPersonalizeFee) {
    modalPersonalizeFee.textContent = '+' + formatMoney(feePrice);
  }

  var productCache = {};
  var currentProduct = null;
  var currentVariant = null;
  var currentOptions = {};

  function openModal() {
    if (modal) {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }
  }
  function closeModal() {
    if (modal) {
      modal.hidden = true;
      document.body.style.overflow = '';
    }
  }

  $$('[data-cs-modal-close]').forEach(function (el) {
    el.addEventListener('click', closeModal);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  function updateModalPrice() {
    if (!modalPrice) return;
    var match = currentVariant;
    if (!match) { modalPrice.textContent = 'Unavailable'; return; }
    var personalized = !!(modalPersonalizeEnabled && modalPersonalizeEnabled.checked);
    var total = match.price + (personalized ? feePrice : 0);
    var compare = match.compare_at_price || 0;
    if (compare > 0) compare = compare + (personalized ? feePrice : 0);
    var html = formatMoney(total);
    if (compare > total) {
      html += ' <span class="cs-modal__price-compare">' + formatMoney(compare) + '</span>';
    }
    if (personalized && feePrice > 0) {
      html += ' <span class="cs-modal__price-fee">(incl. +' + formatMoney(feePrice) + ' personalization)</span>';
    }
    modalPrice.innerHTML = html;
  }

  function selectVariant() {
    if (!currentProduct) return;
    var opts = (currentProduct.options || []).map(function (o) {
      if (o == null) return '';
      if (typeof o === 'string') return o;
      return o.name || '';
    });
    var match = null;
    for (var i = 0; i < currentProduct.variants.length; i++) {
      var v = currentProduct.variants[i];
      var ok = true;
      for (var j = 0; j < opts.length; j++) {
        var name = opts[j];
        if (currentOptions[name] && v['option' + (j + 1)] !== currentOptions[name]) {
          ok = false; break;
        }
      }
      if (ok) { match = v; break; }
    }
    currentVariant = match;

    updateModalPrice();

    if (modalAdd) {
      var labelEl = modalAdd.querySelector('.cs-modal__add-label');
      if (match && match.available) {
        modalAdd.disabled = false;
        modalAdd.classList.remove('is-disabled');
        if (labelEl) labelEl.textContent = 'Add to Cart';
      } else {
        modalAdd.disabled = true;
        modalAdd.classList.add('is-disabled');
        if (labelEl) labelEl.textContent = match ? 'Sold Out' : 'Unavailable';
      }
    }

    // Swap image if the variant has one
    if (match && match.featured_image && match.featured_image.src && modalImage) {
      modalImage.src = match.featured_image.src.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_800x.$1$2');
    }

    // Update swatch active classes
    $$('.cs-modal-opt', modalOptions).forEach(function (row) {
      var optName = row.getAttribute('data-option-name');
      $$('.cs-modal-opt__btn', row).forEach(function (b) {
        b.classList.toggle('is-active', currentOptions[optName] === b.getAttribute('data-value'));
      });
    });
  }

  function renderOptions(product) {
    if (!modalOptions) return;
    modalOptions.innerHTML = '';
    // Shopify's /products/{handle}.js returns `options` as either an array of
    // strings (older) or an array of objects {name, position, values} (newer).
    // Normalize to an array of name strings.
    var optionNames = (product.options || []).map(function (o) {
      if (o == null) return '';
      if (typeof o === 'string') return o;
      return o.name || '';
    });
    optionNames.forEach(function (optName, idx) {
      var optValues = [];
      product.variants.forEach(function (v) {
        var val = v['option' + (idx + 1)];
        if (val && optValues.indexOf(val) === -1) optValues.push(val);
      });

      var row = document.createElement('div');
      row.className = 'cs-modal-opt';
      row.setAttribute('data-option-name', optName);

      var label = document.createElement('div');
      label.className = 'cs-modal-opt__label';
      label.innerHTML = '<strong>' + escapeHtml(optName) + ':</strong> <span data-opt-value>' + escapeHtml(optValues[0] || '') + '</span>';
      row.appendChild(label);

      var isColor = /colou?r/i.test(optName);
      var btns = document.createElement('div');
      btns.className = isColor ? 'cs-modal-opt__swatches' : 'cs-modal-opt__pills';
      optValues.forEach(function (val) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'cs-modal-opt__btn';
        if (isColor) {
          b.className += ' cs-modal-opt__btn--swatch';
          b.setAttribute('data-color', val.toLowerCase().replace(/\s+/g, '-'));
          b.title = val;
          var hex = swatchHex(val);
          if (hex) {
            b.style.background = hex;
            // White/very-light swatches need a visible border
            if (/^#(f|e)[0-9a-f]/i.test(hex) || hex === '#ffffff') {
              b.style.borderColor = '#d1d5db';
            }
          } else {
            // Fallback: striped grey with a small label inside so it's never truly blank
            b.style.background = 'repeating-linear-gradient(45deg,#e5e7eb,#e5e7eb 4px,#f3f4f6 4px,#f3f4f6 8px)';
          }
        } else {
          b.textContent = val;
        }
        b.setAttribute('data-value', val);
        b.addEventListener('click', function () {
          currentOptions[optName] = val;
          label.querySelector('[data-opt-value]').textContent = val;
          selectVariant();
        });
        btns.appendChild(b);
      });
      row.appendChild(btns);
      modalOptions.appendChild(row);

      // Default to first value
      currentOptions[optName] = optValues[0];
    });
  }

  function renderThumbs(product) {
    if (!modalThumbs) return;
    modalThumbs.innerHTML = '';
    (product.images || []).slice(0, 6).forEach(function (src, idx) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cs-modal__thumb' + (idx === 0 ? ' is-active' : '');
      var img = document.createElement('img');
      img.src = src.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_200x.$1$2');
      img.alt = '';
      img.loading = 'lazy';
      b.appendChild(img);
      b.addEventListener('click', function () {
        if (modalImage) modalImage.src = src.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_800x.$1$2');
        $$('.cs-modal__thumb', modalThumbs).forEach(function (t) { t.classList.remove('is-active'); });
        b.classList.add('is-active');
      });
      modalThumbs.appendChild(b);
    });
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (m) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[m];
    });
  }

  function loadProduct(handle, cardEl) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { productCache[handle] = data; return data; });
  }

  function openProduct(handle, cardEl) {
    openModal();

    // Reset modal state
    currentProduct = null;
    currentVariant = null;
    currentOptions = {};
    if (modalQtyInput) modalQtyInput.value = 1;
    if (modalPersonalizeEnabled) modalPersonalizeEnabled.checked = false;
    if (modalPersonalizeBody) modalPersonalizeBody.hidden = true;
    if (modalText) modalText.value = '';
    if (modalCharCount) modalCharCount.textContent = '0';
    if (modalPreview) modalPreview.hidden = true;
    if (modalPreviewText) modalPreviewText.textContent = '';

    // Eager update from card
    var img = cardEl && cardEl.querySelector('img');
    if (modalImage && img) modalImage.src = img.src;
    var titleEl = cardEl && cardEl.querySelector('.cs-product__title');
    if (modalTitle) modalTitle.textContent = titleEl ? titleEl.textContent : '';
    if (modalOptions) modalOptions.innerHTML = '<div class="cs-modal__loading">Loading…</div>';
    if (modalPrice) modalPrice.textContent = '';

    // Personalization UI visibility — shown whenever the product is tagged
    // personalizable. If no fee product is configured, we still collect the
    // personalization properties and skip the fee line item.
    var personalizable = cardEl && cardEl.getAttribute('data-personalizable') === '1';
    if (modalPersonalize) modalPersonalize.hidden = !personalizable;
    if (modalPersonalizeFee) {
      modalPersonalizeFee.hidden = !(feePrice > 0);
    }

    loadProduct(handle).then(function (p) {
      currentProduct = p;
      if (modalTitle) modalTitle.textContent = p.title;
      if (modalDescription) modalDescription.innerHTML = p.description || '';
      renderThumbs(p);
      renderOptions(p);
      selectVariant();
    }).catch(function (err) {
      console.error('cs: failed to load product', err);
      if (modalOptions) modalOptions.innerHTML = '<div class="cs-modal__loading">Could not load product.</div>';
    });
  }

  // Wire up product card clicks
  products.forEach(function (card) {
    card.addEventListener('click', function (e) {
      e.preventDefault();
      var handle = card.getAttribute('data-cs-product-open');
      if (handle) openProduct(handle, card);
    });
  });

  // Quantity stepper
  if (modalQtyMinus) modalQtyMinus.addEventListener('click', function () {
    var n = Math.max(1, parseInt(modalQtyInput.value, 10) - 1 || 1);
    modalQtyInput.value = n;
  });
  if (modalQtyPlus) modalQtyPlus.addEventListener('click', function () {
    var n = Math.max(1, parseInt(modalQtyInput.value, 10) + 1 || 2);
    modalQtyInput.value = n;
  });

  // Personalization UI
  function syncPersonalizationPreview() {
    if (!modalPreview) return;
    var on = !!(modalPersonalizeEnabled && modalPersonalizeEnabled.checked);
    var text = (modalText && modalText.value) || '';
    modalPreview.hidden = !(on && text);
    if (modalPreviewText) {
      modalPreviewText.textContent = text;
      modalPreviewText.style.fontFamily = modalFont ? modalFont.value : 'inherit';
    }
  }

  if (modalPersonalizeEnabled) {
    modalPersonalizeEnabled.addEventListener('change', function () {
      if (modalPersonalizeBody) modalPersonalizeBody.hidden = !modalPersonalizeEnabled.checked;
      syncPersonalizationPreview();
      updateModalPrice();
    });
  }
  if (modalText) {
    modalText.addEventListener('input', function () {
      var v = modalText.value || '';
      if (modalCharCount) modalCharCount.textContent = v.length;
      syncPersonalizationPreview();
    });
  }
  if (modalFont) {
    modalFont.addEventListener('change', syncPersonalizationPreview);
  }

  // Add to cart
  if (modalAdd) {
    modalAdd.addEventListener('click', function () {
      if (!currentVariant || !currentVariant.available) return;
      var qty = Math.max(1, parseInt(modalQtyInput && modalQtyInput.value, 10) || 1);

      var items = [];
      var properties = {};
      if (modalPersonalizeEnabled && modalPersonalizeEnabled.checked && modalText && modalText.value.trim()) {
        properties['Personalization'] = modalText.value.trim();
        properties['Font'] = modalFont ? modalFont.value : '';
      }
      items.push({ id: currentVariant.id, quantity: qty, properties: properties });

      if (properties['Personalization'] && feeVariantId) {
        items.push({
          id: parseInt(feeVariantId, 10),
          quantity: qty,
          properties: { '_linked_to': currentProduct ? currentProduct.handle : '' }
        });
      }
      // If no fee product is configured, we still submit the personalization
      // properties on the main line — just without the separate fee line.

      modalAdd.disabled = true;
      var addLabelEl = modalAdd.querySelector('.cs-modal__add-label');
      var originalLabel = addLabelEl ? addLabelEl.textContent : 'Add to Cart';
      if (addLabelEl) addLabelEl.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      }).then(function () {
        return refreshCart();
      }).then(function () {
        closeModal();
        openDrawer();
      }).catch(function (err) {
        console.error('cs: add to cart failed', err);
        alert((err && err.description) || 'Could not add to cart.');
      }).finally(function () {
        modalAdd.disabled = false;
        if (addLabelEl) addLabelEl.textContent = originalLabel;
      });
    });
  }

  // ─────────────────────────────────────────────
  //  CART DRAWER
  // ─────────────────────────────────────────────
  var drawer = $('[data-cs-drawer]');
  var drawerItems = $('[data-cs-drawer-items]');
  var drawerEmpty = $('[data-cs-drawer-empty]');
  var drawerSubtotal = $('[data-cs-drawer-subtotal]');
  var cartCountEl = $('[data-cs-cart-count]');

  function openDrawer() {
    if (drawer) {
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
    }
  }
  function closeDrawer() {
    if (drawer) {
      drawer.hidden = true;
      document.body.style.overflow = '';
    }
  }
  $$('[data-cs-drawer-close]').forEach(function (el) {
    el.addEventListener('click', closeDrawer);
  });
  $$('[data-cs-cart-open]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      refreshCart().then(openDrawer);
    });
  });

  // Force "Proceed to Checkout" to open in a new tab via window.open.
  // Some third-party apps (e.g. minimum-order-value plugins) install global
  // click listeners that intercept navigation to /checkout and show a popup
  // blocking the shopper. A new tab opened via window.open() breaks out of
  // the current document's handler chain — Shopify's hosted checkout runs
  // the payment page, not the theme, so those interceptors can't follow us.
  $$('[data-cs-checkout]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var win = window.open('/checkout', '_blank', 'noopener');
      if (!win) {
        // Popup blocker killed it — fall back to same-tab navigation so the
        // shopper isn't stuck. They'll see the app's popup but at least the
        // button still works.
        window.location.href = '/checkout';
      }
    }, true); // capture phase — runs before any bubbling handlers the app attached
  });

  function isPersonalizationFeeItem(item) {
    if (!item) return false;
    // Explicitly marked by our add-to-cart flow
    if (item.properties && item.properties._linked_to) return true;
    // Matches the configured fee product (by variant id)
    if (feeVariantId && String(item.variant_id) === String(feeVariantId)) return true;
    if (feeVariantId && String(item.id) === String(feeVariantId)) return true;
    // Title-based fallback so stale / externally-added fee lines are still hidden
    var title = (item.product_title || '').toLowerCase();
    if (title === 'personalization fee') return true;
    if (title === 'personalization') return true;
    if (title === 'add personalization') return true;
    var handle = (item.handle || '').toLowerCase();
    if (handle === 'personalization-fee' || handle === 'personalization' || handle === 'add-personalization') return true;
    return false;
  }

  function renderLineItem(item) {
    var wrap = document.createElement('div');
    wrap.className = 'cs-drawer-item';
    wrap.setAttribute('data-key', item.key);

    // Hide the fee product itself — it's shown as an annotation on the
    // linked product's row and still contributes to the cart subtotal
    // (which comes straight from Shopify's cart.total_price).
    if (isPersonalizationFeeItem(item)) {
      wrap.classList.add('cs-drawer-item--hidden');
    }

    var imgHtml = item.image
      ? '<img src="' + item.image.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_200x.$1$2') + '" alt="">'
      : '';

    var propsHtml = '';
    var personalizationLabel = '';
    var fontLabel = '';
    if (item.properties) {
      for (var k in item.properties) {
        if (!Object.prototype.hasOwnProperty.call(item.properties, k)) continue;
        if (k.charAt(0) === '_') continue; // hidden prop
        var val = item.properties[k];
        if (k === 'Personalization') personalizationLabel = val;
        else if (k === 'Font') fontLabel = val;
        else propsHtml += '<div class="cs-drawer-item__prop">' + escapeHtml(k) + ': ' + escapeHtml(String(val)) + '</div>';
      }
    }
    if (personalizationLabel) {
      var feeStr = feePrice > 0 ? ' <span class="cs-drawer-item__fee">(+' + formatMoney(feePrice) + ')</span>' : '';
      propsHtml += '<div class="cs-drawer-item__prop">Personalization: "' + escapeHtml(personalizationLabel) + '"' + feeStr + '</div>';
      if (fontLabel) propsHtml += '<div class="cs-drawer-item__prop">Font: ' + escapeHtml(fontLabel) + '</div>';
    }

    var variantLabel = '';
    if (item.variant_title) variantLabel = escapeHtml(item.variant_title);

    wrap.innerHTML =
      '<div class="cs-drawer-item__media">' + imgHtml + '</div>' +
      '<div class="cs-drawer-item__body">' +
        '<div class="cs-drawer-item__title">' + escapeHtml(item.product_title) + '</div>' +
        (variantLabel ? '<div class="cs-drawer-item__variant">' + variantLabel + '</div>' : '') +
        propsHtml +
        '<div class="cs-drawer-item__price">' + formatMoney(item.final_line_price) + '</div>' +
        '<div class="cs-drawer-item__qty">' +
          '<button type="button" data-qty-minus>−</button>' +
          '<input type="number" value="' + item.quantity + '" min="0">' +
          '<button type="button" data-qty-plus>+</button>' +
        '</div>' +
      '</div>';

    var qtyInput = wrap.querySelector('input');
    wrap.querySelector('[data-qty-minus]').addEventListener('click', function () {
      updateLine(item.key, Math.max(0, item.quantity - 1));
    });
    wrap.querySelector('[data-qty-plus]').addEventListener('click', function () {
      updateLine(item.key, item.quantity + 1);
    });
    qtyInput.addEventListener('change', function () {
      updateLine(item.key, Math.max(0, parseInt(qtyInput.value, 10) || 0));
    });

    return wrap;
  }

  function updateLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); });
  }

  // Keep fee line quantities in sync with their linked personalized items.
  // When the shopper removes / decrements a personalized line, Shopify leaves
  // the fee line in place — this reconciles it (and drops orphans) so the
  // subtotal stays correct.
  function reconcileFeeLines(cart) {
    if (!cart || !cart.items || !cart.items.length) return Promise.resolve(cart);

    var needed = {};
    cart.items.forEach(function (item) {
      if (item.properties && item.properties._linked_to) return;
      if (item.properties && item.properties.Personalization) {
        var h = item.handle || '';
        needed[h] = (needed[h] || 0) + item.quantity;
      }
    });

    var updates = [];
    cart.items.forEach(function (item) {
      if (!item.properties || !item.properties._linked_to) return;
      var expected = needed[item.properties._linked_to] || 0;
      if (expected !== item.quantity) {
        updates.push({ key: item.key, quantity: expected });
      }
    });

    if (!updates.length) return Promise.resolve(cart);

    return updates.reduce(function (p, u) {
      return p.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: u.key, quantity: u.quantity })
        }).then(function (r) { return r.json(); });
      });
    }, Promise.resolve()).then(function (lastCart) {
      return lastCart || cart;
    });
  }

  function refreshCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(reconcileFeeLines)
      .then(function (cart) {
        if (cartCountEl) cartCountEl.textContent = cart.item_count;
        if (drawerItems) {
          drawerItems.innerHTML = '';
          if (cart.items.length === 0) {
            if (drawerEmpty) drawerEmpty.hidden = false;
          } else {
            if (drawerEmpty) drawerEmpty.hidden = true;
            cart.items.forEach(function (item) {
              drawerItems.appendChild(renderLineItem(item));
            });
          }
        }
        if (drawerSubtotal) drawerSubtotal.textContent = formatMoney(cart.total_price);
        return cart;
      });
  }

  // ─────────────────────────────────────────────
  //  HELP MODAL
  // ─────────────────────────────────────────────
  var helpBtn = $('[data-cs-help-open]');
  var helpModal = $('[data-cs-help-modal]');

  function openHelp() {
    if (helpModal) helpModal.hidden = false;
  }
  function closeHelp() {
    if (helpModal) helpModal.hidden = true;
  }
  if (helpBtn) helpBtn.addEventListener('click', openHelp);
  $$('[data-cs-help-close]').forEach(function (el) {
    el.addEventListener('click', closeHelp);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && helpModal && !helpModal.hidden) closeHelp();
  });

  // ─────────────────────────────────────────────
  //  INIT
  // ─────────────────────────────────────────────
  updateCounts();
  apply();
  refreshCart();
})();
